import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { getOrgs, createOrg, addOrgAppMapping } from '../../../store/orgs';
import { getCognitoUsers } from '../../../store/cognito';
import { getDemandResources } from '../../../store/demand';
import { getSolarResources } from '../../../store/solar';
import { getManagementOrganizations } from '../../../store/management';
import orchestrateAuthApi from '../../../api/orchestrateAuth';
import { toastr } from '../../../components/CustomToast';

// ─── CreateOrgDialog ──────────────────────────────────────────────────────────

function CreateOrgDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await dispatch(createOrg(name.trim()));
    setName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create Organization</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label='Organization Name'
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AddMappingDialog ─────────────────────────────────────────────────────────

const APP_OPTIONS = [
  { value: 'demand', label: 'Gridcap' },
  { value: 'solar', label: 'LightLevel' },
  { value: 'energytracer', label: 'EnergyTracer' },
  { value: 'management', label: 'EventSync' },
];

function AddMappingDialog({ open, orgId, onClose }) {
  const dispatch = useDispatch();
  const demandOrgs = useSelector((state) => state.demand.organizations);
  const solarOrgs = useSelector((state) => state.solar.organizations);
  const managementOrgs = useSelector((state) => state.management.organizations);

  const [appName, setAppName] = useState('demand');
  const [appOrgId, setAppOrgId] = useState('');

  const appOrgs =
    appName === 'demand' ? demandOrgs
    : appName === 'solar' ? solarOrgs
    : appName === 'management' ? managementOrgs
    : null;
  const hasOrgSelect = appOrgs !== null && appOrgs.length > 0;

  const handleAppChange = (e) => {
    setAppName(e.target.value);
    setAppOrgId('');
  };

  const handleSubmit = async () => {
    if (!appOrgId.trim()) return;
    await dispatch(addOrgAppMapping({ orgId, appName, appOrgId: appOrgId.trim() }));
    setAppOrgId('');
    onClose();
  };

  const sortedOrgs = appOrgs
    ? [...appOrgs].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add App Mapping</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2, mt: 0 }}>
        <TextField
          select
          size='small'
          label='App'
          fullWidth
          value={appName}
          onChange={handleAppChange}
          sx={{ mt: 1 }}>
          {APP_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        {hasOrgSelect ? (
          <TextField
            select
            size='small'
            label='Organization'
            fullWidth
            value={appOrgId}
            onChange={(e) => setAppOrgId(e.target.value)}>
            {sortedOrgs.map((org) => (
              <MenuItem key={org.org_id} value={org.org_id}>
                <Box>
                  <Typography variant='body2'>{org.name}</Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'monospace' }}>
                    {org.org_id}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            size='small'
            label='App Org ID'
            fullWidth
            value={appOrgId}
            onChange={(e) => setAppOrgId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            helperText={appOrgs !== null && appOrgs.length === 0 ? 'Loading orgs…' : undefined}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!appOrgId.trim()}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AddMemberDialog ──────────────────────────────────────────────────────────

function AddMemberDialog({ open, orgId, existingMembers, onClose }) {
  const cognitoUsers = useSelector((state) => state.cognito.users);
  const [userSub, setUserSub] = useState('');
  const [role, setRole] = useState('member');
  const [saving, setSaving] = useState(false);

  const existingSubs = new Set((existingMembers || []).map((m) => m.user_sub));

  const availableUsers = [...cognitoUsers]
    .filter((u) => u.sub && !existingSubs.has(u.sub))
    .sort((a, b) => (a.email || '').localeCompare(b.email || ''));

  const handleSubmit = async () => {
    if (!userSub) return;
    setSaving(true);
    try {
      await orchestrateAuthApi.orgs.addMember(orgId, userSub, role);
      toastr.success({ title: 'Member added' });
      setUserSub('');
      setRole('member');
      onClose();
    } catch (err) {
      console.error('ERROR :: addMember', err);
      toastr.error({ title: 'Error', message: 'Failed to add member' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2, mt: 0 }}>
        <TextField
          select
          size='small'
          label='User'
          fullWidth
          sx={{ mt: 1 }}
          value={userSub}
          onChange={(e) => setUserSub(e.target.value)}>
          {availableUsers.length === 0 ? (
            <MenuItem disabled>No users available</MenuItem>
          ) : (
            availableUsers.map((u) => (
              <MenuItem key={u.sub} value={u.sub}>
                {u.email || u.sub}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          select
          size='small'
          label='Role'
          fullWidth
          value={role}
          onChange={(e) => setRole(e.target.value)}>
          <MenuItem value='member'>Member</MenuItem>
          <MenuItem value='admin'>Admin</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!userSub || saving}>
          {saving ? <CircularProgress size={18} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── OrgRow ───────────────────────────────────────────────────────────────────

function OrgRow({ org }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);

  const cognitoUsers = useSelector((state) => state.cognito.users);

  const fetchDetail = useCallback(() => {
    setDetailLoading(true);
    orchestrateAuthApi.orgs
      .get(org.org_id)
      .then((res) => setDetail(res.data))
      .catch((err) => console.error('ERROR :: loadOrgDetail', err))
      .finally(() => setDetailLoading(false));
  }, [org.org_id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleRemoveMember = async (userSub) => {
    setRemovingMember(userSub);
    try {
      await orchestrateAuthApi.orgs.removeMember(org.org_id, userSub);
      toastr.success({ title: 'Member removed' });
      fetchDetail();
    } catch (err) {
      console.error('ERROR :: removeMember', err);
      toastr.error({ title: 'Error', message: 'Failed to remove member' });
    } finally {
      setRemovingMember(null);
    }
  };

  const emailBySub = Object.fromEntries(
    cognitoUsers.filter((u) => u.sub).map((u) => [u.sub, u.email || u.sub])
  );

  const apps = detail?.apps || [];
  const members = detail?.members || [];

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <IconButton size='small'>
            <FontAwesomeIcon
              icon={['fal', expanded ? 'chevron-up' : 'chevron-down']}
              size='xs'
            />
          </IconButton>
        </TableCell>
        <TableCell>{org.name}</TableCell>
        <TableCell>
          <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: 11 }}>
            {org.org_id}
          </Typography>
        </TableCell>
        <TableCell align='center'>
          {detailLoading ? <CircularProgress size={12} /> : apps.length}
        </TableCell>
        <TableCell align='center'>
          {detailLoading ? <CircularProgress size={12} /> : members.length}
        </TableCell>
        <TableCell>
          {org.last_update ? new Date(org.last_update).toLocaleDateString() : '—'}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ px: 4, py: 2, bgcolor: 'grey.50' }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant='body2' color='text.secondary'>Loading…</Typography>
                </Box>
              ) : (
                <>
                  {/* App Mappings */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant='subtitle2'>App Mappings</Typography>
                    <Button
                      size='small'
                      startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
                      onClick={(e) => { e.stopPropagation(); setMappingDialogOpen(true); }}>
                      Add
                    </Button>
                  </Box>
                  {apps.length === 0 ? (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                      No app mappings yet
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {apps.map((a) => (
                        <Chip
                          key={a.type}
                          label={`${a.app_name}: ${a.app_org_id}`}
                          size='small'
                          variant='outlined'
                        />
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Members */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant='subtitle2'>Members</Typography>
                    <Button
                      size='small'
                      startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
                      onClick={(e) => { e.stopPropagation(); setMemberDialogOpen(true); }}>
                      Add
                    </Button>
                  </Box>
                  {members.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                      No members yet
                    </Typography>
                  ) : (
                    <Table size='small' sx={{ maxWidth: 520 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell width={40} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {members.map((m) => (
                          <TableRow key={m.user_sub}>
                            <TableCell>
                              <Typography variant='body2'>
                                {emailBySub[m.user_sub] || m.user_sub}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2' color='text.secondary'>
                                {m.role || 'member'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size='small'
                                disabled={removingMember === m.user_sub}
                                onClick={(e) => { e.stopPropagation(); handleRemoveMember(m.user_sub); }}>
                                {removingMember === m.user_sub ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <FontAwesomeIcon icon={['fal', 'trash']} size='xs' />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <AddMappingDialog
        open={mappingDialogOpen}
        orgId={org.org_id}
        onClose={() => { setMappingDialogOpen(false); fetchDetail(); }}
      />
      <AddMemberDialog
        open={memberDialogOpen}
        orgId={org.org_id}
        existingMembers={members}
        onClose={() => { setMemberDialogOpen(false); fetchDetail(); }}
      />
    </>
  );
}

// ─── Organizations ────────────────────────────────────────────────────────────

export default function Organizations() {
  const dispatch = useDispatch();
  const { orgs } = useSelector((state) => state.orgs);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(getOrgs());
    dispatch(getCognitoUsers());
    dispatch(getDemandResources());
    dispatch(getSolarResources());
    dispatch(getManagementOrganizations());
  }, [dispatch]);

  const filteredOrgs = useMemo(() => {
    const sorted = [...orgs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!search.trim()) return sorted;
    const q = search.trim().toLowerCase();
    return sorted.filter(
      (o) =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.org_id || '').toLowerCase().includes(q)
    );
  }, [orgs, search]);

  return (
    <Grid container justifyContent='center' sx={{ mt: 1 }}>
      <Grid size={{ xs: 12 }} sx={{ p: '20px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='h6'>Organizations</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size='small'
              placeholder='Search…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 220 }}
            />
            <Button
              variant='contained'
              startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
              onClick={() => setCreateDialogOpen(true)}>
              New Org
            </Button>
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Name</TableCell>
                <TableCell>Org ID</TableCell>
                <TableCell align='center' width={60}>Apps</TableCell>
                <TableCell align='center' width={80}>Members</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center' sx={{ color: 'text.secondary', py: 4 }}>
                    {orgs.length === 0 ? 'No organizations yet' : 'No results'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map((org) => <OrgRow key={org.org_id} org={org} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <CreateOrgDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Grid>
  );
}